import { useCallback, useEffect, useRef, useState } from "react";
import {
  apiApont,
  type ApontResolver,
  type ApontMotivo,
  type PcfDetalhe,
  type TipoAcao,
} from "../lib/api";
import { Logo } from "../components/Logo";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";
import { TimelineEtapas } from "../components/PcfTimelineModal";
import { PcfRoteiroModal } from "../components/PcfRoteiroModal";
import { PcfImpressao, carregarR1Publico } from "./PcfImpressao";
import { MsgModal } from "../components/MsgModal";
import { useTheme } from "../hooks/useTheme";
import { useNow } from "../hooks/useNow";
import { LancamentoProdutoModal } from "../components/LancamentoProdutoModal";
import { SeparacaoMaterialModal } from "../components/SeparacaoMaterialModal";
import { SenhaEncarregadoModal, type EncarregadoToken } from "../components/SenhaEncarregadoModal";
import { Confirma } from "../components/EstoqueUi";
import type { LancamentoProduto, SeparacaoTroca } from "../lib/api";

interface Props {
  usuarioNome: string;
  onVoltarPainel: () => void;
  modoEstacao?: boolean; // quiosque de chão de fábrica: sem login/botão Painel
}

type Msg = { tipo: "erro" | "ok" | "info"; texto: string } | null;

// Conversão fiel do legado acompanhamento_pcf: apontamento de chão de fábrica por
// leitor de código de barras. Fluxo: escaneia etiqueta → senha do operador → ação.
export function Apontamento({ usuarioNome, onVoltarPainel, modoEstacao }: Props) {
  const { theme, toggle } = useTheme();
  const agora = useNow();

  const [codbar, setCodbar] = useState("");
  const [resolve, setResolve] = useState<ApontResolver | null>(null);
  const [senha, setSenha] = useState("");
  const [operador, setOperador] = useState<string | null>(null);
  const [msg, setMsg] = useState<Msg>(null);
  const [busy, setBusy] = useState(false);
  const [pcfDet, setPcfDet] = useState<PcfDetalhe | null>(null);

  const [motivosOpen, setMotivosOpen] = useState(false);
  const [motivos, setMotivos] = useState<ApontMotivo[]>([]);
  const [etapasAbertas, setEtapasAbertas] = useState<{ descri: string; situac: string }[] | null>(null);
  const ordHist = useOrdenacao(resolve?.historico, {
    eta: (h) => h.etapa, sit: (h) => h.situac, op: (h) => h.operador,
    data: (h) => (h.data ? `${h.data} ${h.hora ?? ""}`.trim() : null), hora: (h) => h.hora, tempo: (h) => h.tempo, mot: (h) => h.codmot || null,
  });
  const ordAb = useOrdenacao(etapasAbertas, { eta: (e) => e.descri, sit: (e) => e.situac });
  const [roteiroOpen, setRoteiroOpen] = useState(false);
  const [perguntaReabrir, setPerguntaReabrir] = useState(false); // L617-621 Sim/Não
  const [motivoTexto, setMotivoTexto] = useState<string | null>(null); // tela-3
  const [imprimirPcf, setImprimirPcf] = useState<number | null>(null); // pcf_etapas IMPRIMIR
  // lancamento_produtos (legado novo): Finalizar da Inspeção Final sem lotes abre o lançamento do produto acabado
  const [lancando, setLancando] = useState(false);
  const [semLancamento, setSemLancamento] = useState(false);
  // decisão 24/10/2026: Finalizar da SEPARAÇÃO com material empenhado abre a confirmação/troca do lote
  const [separando, setSeparando] = useState(false);
  // decisão 25/10/2026: finalização da última etapa que baixa material empenhado pede a senha do encarregado
  const [encPend, setEncPend] = useState<{ codmot?: number; lancamento?: LancamentoProduto | null; trocas?: SeparacaoTroca[] } | null>(null);
  const [erroEnc, setErroEnc] = useState<string | null>(null);

  const refCodbar = useRef<HTMLInputElement>(null);
  const refSenha = useRef<HTMLInputElement>(null);
  const refPrimeiroBotao = useRef<HTMLButtonElement>(null);

  const focarLeitor = useCallback(() => {
    setTimeout(() => refCodbar.current?.focus(), 30);
  }, []);

  useEffect(() => {
    focarLeitor();
  }, [focarLeitor]);

  const limpar = useCallback(() => {
    setCodbar("");
    setSenha("");
    setResolve(null);
    setOperador(null);
    setMsg(null);
    setPcfDet(null);
    setEtapasAbertas(null);
    focarLeitor();
  }, [focarLeitor]);

  // Carrega a timeline visual da PCF (rota pública do apontamento — sem login).
  const carregarPcf = useCallback((numpcf: number) => {
    setPcfDet(null);
    apiApont.pcfDetalhe(numpcf).then(setPcfDet).catch(() => setPcfDet(null));
  }, []);

  const onScan = useCallback(async () => {
    const codigo = Number(codbar.replace(/\D+/g, ""));
    if (!codigo) return;
    setBusy(true);
    setMsg(null);
    setOperador(null);
    setSenha("");
    try {
      const r = await apiApont.resolver(codigo);
      setResolve(r);
      carregarPcf(r.numpcf);
      if (r.erro) {
        setMsg({ tipo: "erro", texto: r.erro });
        // TELA-4 do legado: etapas ainda em aberto (MONTAR-TABELA).
        if (r.abrirEtapasAbertas) setEtapasAbertas(r.etapasAbertas ?? []);
        focarLeitor();
      } else if (r.perguntarReabrir) {
        // L617-621: "Etapa pulada! Deseja reabrir a etapa?" — antes da senha.
        setPerguntaReabrir(true);
      } else {
        setMsg(null);
        setTimeout(() => refSenha.current?.focus(), 30);
      }
    } catch (e) {
      setMsg({ tipo: "erro", texto: (e as Error).message });
      focarLeitor();
    } finally {
      setBusy(false);
    }
  }, [codbar, carregarPcf, focarLeitor]);

  const onSenha = useCallback(async (senhaArg?: string) => {
    const s = (senhaArg ?? senha).trim();
    if (!resolve || !s) return;
    setBusy(true);
    try {
      const r = await apiApont.operador(s);
      if (!r.ok) {
        setMsg({ tipo: "erro", texto: r.erro ?? "Senha inválida." });
        setOperador(null);
        setTimeout(() => refSenha.current?.focus(), 30);
      } else {
        setOperador(r.operador ?? "");
        // VER-SENHA L683-688: PCF FINALIZADA não habilita nenhum botão.
        setMsg(resolve.pcfSituac === "FINALIZADA" ? { tipo: "erro", texto: "PCF finalizada — nenhuma ação disponível." } : null);
        setTimeout(() => refPrimeiroBotao.current?.focus(), 30);
      }
    } catch (e) {
      setMsg({ tipo: "erro", texto: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }, [resolve, senha]);

  const executar = useCallback(
    async (acao: TipoAcao, codmot?: number, lancamento?: LancamentoProduto | null, trocas?: SeparacaoTroca[], encarregado?: EncarregadoToken) => {
      if (!resolve || !operador) return;
      if (acao === "finalizar" && resolve.separarMaterial && trocas === undefined) {
        setSeparando(true);
        return;
      }
      // ac:846-856: Inspeção Final sem cadpcf_etapas_lotes -> abre o lançamento antes de finalizar
      if (acao === "finalizar" && resolve.lancarProduto && lancamento === undefined) {
        setLancando(true);
        return;
      }
      setBusy(true);
      setMsg(null);
      try {
        const r = await apiApont.acao(acao, {
          numpcf: resolve.numpcf,
          posicao: resolve.posicao,
          senha,
          codmot,
          ...(lancamento ? { lancamento } : {}),
          ...(trocas ? { separacao: { trocas } } : {}),
          ...(encarregado !== undefined ? { encarregado } : {}), // reunião 28/10/2026: encarregado + token
        });
        if (!r.ok && r.precisaEncarregado && acao === "finalizar") {
          // 1ª vez abre o modal sem mensagem; token recusado mostra o erro dentro do modal (nada foi gravado)
          setEncPend({ codmot, lancamento, trocas });
          setErroEnc(encarregado !== undefined ? r.erro ?? "Token do encarregado inválido." : null);
        } else if (!r.ok) {
          setEncPend(null);
          setMsg({ tipo: "erro", texto: r.erro ?? "Falha na operação." });
        } else {
          setEncPend(null);
          setMsg({ tipo: "ok", texto: `${rotuloAcao(acao)} registrado — operador ${r.operador ?? operador}.` });
          limpar();
        }
      } catch (e) {
        setMsg({ tipo: "erro", texto: (e as Error).message });
      } finally {
        setBusy(false);
      }
    },
    [resolve, operador, senha, limpar]
  );

  const abrirMotivos = useCallback(async () => {
    try {
      const ms = await apiApont.motivos();
      setMotivos(ms);
      setMotivosOpen(true);
    } catch (e) {
      setMsg({ tipo: "erro", texto: (e as Error).message });
    }
  }, []);

  // Sim na pergunta da etapa pulada (L622-639): reabre sem senha e refaz a leitura (GO ROTINAS).
  const confirmarReabrir = useCallback(async () => {
    if (!resolve) return;
    setPerguntaReabrir(false);
    setBusy(true);
    try {
      const r = await apiApont.acao("reabrir", { numpcf: resolve.numpcf, posicao: resolve.posicao, senha: "" });
      if (!r.ok) setMsg({ tipo: "erro", texto: r.erro ?? "Falha ao reabrir." });
    } catch (e) {
      setMsg({ tipo: "erro", texto: (e as Error).message });
    } finally {
      setBusy(false);
    }
    await onScan();
  }, [resolve, onScan]);

  // VER-SENHA L683-688: PCF FINALIZADA não habilita nenhum botão (nem o adicionar).
  const pcfFinalizada = resolve?.pcfSituac === "FINALIZADA";
  const podeAgir = !!operador && !busy && !pcfFinalizada;
  // botao-adicionar: só junto com INICIAR / RETOMAR / FINALIZAR-PAUSA (L690-705).
  const podeRoteiro =
    podeAgir && !!resolve && (resolve.acao === "INICIAR" || resolve.acao === "RETOMAR" || resolve.acao === "FINALIZAR_PAUSA");

  return (
    <div className="app apont">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            Etapas de Produção
            <small>Apontamento · chão de fábrica</small>
          </h1>
        </div>
        <span className="spacer" />
        <span className="clock">{agora}</span>
        {!modoEstacao && (
          <div className="user-badge" title={usuarioNome}>
            <span className="user-avatar">{(usuarioNome || "?").charAt(0).toUpperCase()}</span>
            <span className="user-nome">{usuarioNome.split(" ")[0]}</span>
          </div>
        )}
        <button className="btn-secundario" onClick={toggle} title="Alternar tema">{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        {!modoEstacao && (
          <button className="btn-secundario btn-voltar-painel" onClick={onVoltarPainel} title="Voltar ao Painel">
            Voltar
          </button>
        )}
      </header>

      <div className="apont-body">
        {/* Coluna de operação */}
        <section className="apont-op">
          {/* Campo de leitura só aparece quando NÃO há PCF ativa (some ao resolver, volta ao limpar). */}
          {(!resolve || resolve.erro) && (
            <div className="apont-scan">
              <label>
                <span>Código de Barras</span>
                <input
                  ref={refCodbar}
                  className="apont-input-scan"
                  inputMode="numeric"
                  value={codbar}
                  onChange={(e) => setCodbar(e.target.value.replace(/\D+/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onScan();
                    }
                  }}
                  placeholder="Escaneie a etiqueta…"
                  autoComplete="off"
                  disabled={busy}
                />
              </label>
            </div>
          )}

          {resolve && !resolve.erro && (
            <div className="apont-cartao">
              <div className="apont-grid">
                {/* Número em destaque do form TELA-1: T1-NUMPCF-WS (ZZZ.ZZZ.ZZZ) + T1-POSICAO-WS (ZZ),
                    preenchidos em TELA-1.prc L532-533 com o nº da PCF e a posição da etapa lida. */}
                <Campo label="Número da PCF" valor={`${resolve.numpcf} ${resolve.posicao || ""}`.trim()} destaque />
                <Campo label="Situação da Etapa" valor={resolve.situacao} badge />
                <Campo label="Etapa atual" valor={resolve.nomeEtapa} wide />
                <Campo label="Produto Final" valor={resolve.nompro || "—"} wide />
              </div>

              {/* Senha compacta + os 3 botões (ação + Limpar + Roteiro) na mesma linha. */}
              <div className="apont-linha-final">
                <label className="apont-senha-mini">
                  <span>Senha do Operador</span>
                  <input
                    ref={refSenha}
                    type="password"
                    inputMode="numeric"
                    maxLength={20}
                    value={senha}
                    onChange={(e) => {
                      // VER-SENHA (TELA-1.prc:415-422/681-687): SENHA-WS X(20), valida no Return. Sem auto-envio aos 4
                      // dígitos: há operadores com senha de 6 dígitos em cad_operadores (o prefixo de 4 podia casar outro).
                      setSenha(e.target.value.slice(0, 20));
                      setOperador(null); // senha mudou: exige nova validação (o servidor revalida a cada ação)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onSenha();
                      }
                    }}
                    autoComplete="off"
                    disabled={busy}
                  />
                </label>
                {operador && <span className="apont-operador" title="Nome do Operador">{operador}</span>}

                {/* botão de ação colado na senha */}
                <div className="apont-acao-principal">
                  {resolve.acao === "INICIAR" && (
                    <>
                      <button ref={refPrimeiroBotao} className="btn-acao verde" disabled={!podeAgir} onClick={() => executar("iniciar")}>Iniciar</button>
                      <button className="btn-acao cinza" disabled={!podeAgir} onClick={() => executar("pular")}>Pular Etapa</button>
                    </>
                  )}
                  {resolve.acao === "RETOMAR" && (
                    <button ref={refPrimeiroBotao} className="btn-acao verde" disabled={!podeAgir} onClick={() => executar("retomar")}>Retomar</button>
                  )}
                  {resolve.acao === "FINALIZAR_PAUSA" && (
                    <>
                      <button ref={refPrimeiroBotao} className="btn-acao azul" disabled={!podeAgir} onClick={() => executar("finalizar")}>Finalizar</button>
                      <button className="btn-acao amarelo" disabled={!podeAgir} onClick={abrirMotivos}>Pausar</button>
                    </>
                  )}
                </div>

                {/* Limpar/Roteiro à direita */}
                <div className="apont-botoes-sec">
                  <button className="btn-secundario" onClick={limpar}>Limpar</button>
                  <button className="btn-secundario" disabled={!podeRoteiro} onClick={() => setRoteiroOpen(true)}>Adicionar Etapa</button>
                </div>
              </div>
            </div>
          )}

          <MsgModal
            texto={msg?.texto}
            tipo={msg?.tipo === "ok" ? "ok" : msg?.tipo === "info" ? "aviso" : "erro"}
            onClose={() => setMsg(null)}
          />

          {(!resolve || resolve.erro) && (
            <div className="apont-rodape-op">
              <button className="btn-secundario" onClick={limpar}>Limpar</button>
            </div>
          )}
        </section>

        {/* Coluna de acompanhamento (timeline + histórico) */}
        <section className="apont-info">
          {/* "Etapa já finalizada" também mostra o histórico (MOSTRAR-TABELA L556/L571). */}
          {resolve && pcfDet && (!resolve.erro || resolve.historico.length > 0) ? (
            <>
              <div className="apont-info-titulo">Progresso da PCF {resolve.numpcf}</div>
              <TimelineEtapas etapas={pcfDet.etapas} />
              <div className="apont-info-titulo">Histórico</div>
              {resolve.historico.length === 0 ? (
                <div className="empty">Nenhum apontamento ainda.</div>
              ) : (
                <table className="apont-hist">
                  <thead>
                    <tr>
                      <ThOrdena ord={ordHist} col="eta">Etapa</ThOrdena>
                      <ThOrdena ord={ordHist} col="sit">Situação</ThOrdena>
                      <ThOrdena ord={ordHist} col="op">Operador</ThOrdena>
                      <ThOrdena ord={ordHist} col="data">Data</ThOrdena>
                      <ThOrdena ord={ordHist} col="hora">Hora</ThOrdena>
                      <ThOrdena ord={ordHist} col="tempo" className="r">Tempo</ThOrdena>
                      <ThOrdena ord={ordHist} col="mot" className="r">Motivo</ThOrdena>
                    </tr>
                  </thead>
                  <tbody>
                    {ordHist.linhas.map((h, i) => (
                      <tr
                        key={i}
                        className={h.codmot ? "clicavel" : undefined}
                        title={h.codmot ? "Ver motivo da pausa" : undefined}
                        onClick={() => h.codmot && setMotivoTexto(`Motivo da pausa : ${h.nommot}`)}
                      >
                        <td>{h.etapa}</td>
                        <td>
                          <span className={`badge-situac situac-${h.situac.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            {h.situac}
                          </span>
                        </td>
                        <td>{h.operador}</td>
                        <td>{h.data}</td>
                        <td>{h.hora}</td>
                        <td className="r">{h.tempo}</td>
                        <td className="r">{h.codmot || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <div className="apont-vazio">
              <p>Escaneie a etiqueta de uma etapa para iniciar o apontamento.</p>
            </div>
          )}
        </section>
      </div>

      {/* Modal de motivo da pausa (TELA-2) */}
      {motivosOpen && (
        <div className="modal-backdrop" onClick={() => setMotivosOpen(false)}>
          <div className="modal-panel modal-estreito" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div>
                <div className="modal-eyebrow">Listagem de Motivos de Pausa na Produção</div>
                <h2>Selecione um dos motivos abaixo</h2>
              </div>
              <button className="btn-secundario" onClick={() => setMotivosOpen(false)}>Voltar</button>
            </header>
            <ul className="motivos-lista">
              {motivos.map((m) => (
                <li key={m.codmot}>
                  <button
                    onClick={() => {
                      setMotivosOpen(false);
                      executar("pausar", m.codmot);
                    }}
                  >
                    {m.nommot}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Modal do roteiro de etapas (PCF_ETAPAS / botao-adicionar) */}
      {roteiroOpen && resolve && (
        <PcfRoteiroModal
          numpcf={resolve.numpcf}
          senha={senha}
          onImprimir={() => setImprimirPcf(resolve.numpcf)}
          onClose={() => {
            setRoteiroOpen(false);
            carregarPcf(resolve.numpcf);
          }}
        />
      )}

      {/* Modal de etapas em aberto (TELA-4) */}
      {etapasAbertas && (
        <div className="modal-backdrop" onClick={() => setEtapasAbertas(null)}>
          <div className="modal-panel modal-estreito" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div>
                <div className="modal-eyebrow">Listagem de Motivos de Pausa na Produção</div>
                <h2>Etapas em aberto que impedem a finalização</h2>
              </div>
              <button className="btn-secundario" onClick={() => setEtapasAbertas(null)}>Voltar</button>
            </header>
            <table className="apont-hist">
              <thead>
                <tr><ThOrdena ord={ordAb} col="eta">Etapa</ThOrdena><ThOrdena ord={ordAb} col="sit">Situação</ThOrdena></tr>
              </thead>
              <tbody>
                {ordAb.linhas.map((e, i) => (
                  <tr key={i}>
                    <td>{e.descri}</td>
                    <td>
                      <span className={`badge-situac situac-${e.situac.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {e.situac}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Etapa pulada: Sim/Não (L617-621) */}
      {perguntaReabrir && (
        <div className="modal-backdrop">
          <div className="modal-panel modal-estreito" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div>
                <div className="modal-eyebrow">Etapa pulada</div>
                <h2>Deseja reabrir a etapa?</h2>
              </div>
            </header>
            <div className="modal-acoes">
              <button className="btn-acao amarelo" autoFocus onClick={() => void confirmarReabrir()}>Sim</button>
              <button
                className="btn-secundario"
                onClick={() => {
                  setPerguntaReabrir(false);
                  setTimeout(() => refSenha.current?.focus(), 30);
                }}
              >
                Não
              </button>
            </div>
          </div>
        </div>
      )}

      {/* tela-3: motivo da pausa */}
      {motivoTexto && (
        <div className="modal-backdrop" onClick={() => setMotivoTexto(null)}>
          <div className="modal-panel modal-estreito" onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div>
                <div className="modal-eyebrow">Motivo de Pausa na Produção</div>
                <h2>{motivoTexto}</h2>
              </div>
              <button className="btn-secundario" autoFocus onClick={() => setMotivoTexto(null)}>Voltar</button>
            </header>
          </div>
        </div>
      )}

      {/* pcf_etapas IMPRIMIR: r1cadpcf */}
      {lancando && resolve && (
        <LancamentoProdutoModal
          numpcf={resolve.numpcf}
          etapa={resolve.nomeEtapa}
          posicao={resolve.posicao}
          onConfirmar={(l) => {
            setLancando(false);
            void executar("finalizar", undefined, l);
          }}
          onVoltar={() => {
            setLancando(false);
            setSemLancamento(true);
          }}
        />
      )}
      {separando && resolve && (
        <SeparacaoMaterialModal
          numpcf={resolve.numpcf}
          onConfirmar={(t) => {
            setSeparando(false);
            void executar("finalizar", undefined, undefined, t);
          }}
          onVoltar={() => setSeparando(false)}
        />
      )}
      {encPend && resolve && (
        <SenhaEncarregadoModal
          numpcf={resolve.numpcf}
          erro={erroEnc}
          onLimparErro={() => setErroEnc(null)}
          onConfirmar={(s) => {
            if (busy) return;
            void executar("finalizar", encPend.codmot, encPend.lancamento, encPend.trocas, s);
          }}
          onVoltar={() => {
            setEncPend(null);
            setErroEnc(null);
          }}
        />
      )}
      {/* lp:660-670: o Voltar do lançamento segue com a finalização SEM lançar o produto — aqui pergunta antes */}
      <Confirma
        texto={semLancamento ? "Finalizar a etapa SEM lançar o produto no estoque?" : null}
        onSim={() => {
          setSemLancamento(false);
          void executar("finalizar", undefined, null);
        }}
        onNao={() => setSemLancamento(false)}
      />
      {imprimirPcf !== null && (
        <div className="modal-backdrop pcf-print-overlay">
          <PcfImpressao numpcf={imprimirPcf} carregar={carregarR1Publico} onVoltar={() => setImprimirPcf(null)} />
        </div>
      )}
    </div>
  );
}

function Campo({ label, valor, destaque, badge, wide }: { label: string; valor: string; destaque?: boolean; badge?: boolean; wide?: boolean }) {
  return (
    <div className={`apont-campo${wide ? " wide" : ""}`}>
      <div className="apont-campo-label">{label}</div>
      <div className={`apont-campo-valor${destaque ? " destaque" : ""}`}>
        {badge ? (
          <span className={`badge-situac situac-${valor.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{valor}</span>
        ) : (
          valor
        )}
      </div>
    </div>
  );
}

function rotuloAcao(a: TipoAcao): string {
  switch (a) {
    case "iniciar": return "Início";
    case "pausar": return "Pausa";
    case "retomar": return "Retomada";
    case "finalizar": return "Finalização";
    case "pular": return "Pulo de etapa";
    case "reabrir": return "Reabertura";
  }
}
